/**
 * App shell for authenticated routes.
 *
 * Three-column flex layout: ActivityBar (48px contour rail) +
 * persistent Sidebar (resizable + can be collapsed to zero width) +
 * the main scroll area. When the sidebar is collapsed, Shell renders
 * a small floating "expand" handle so the user can always get the rail
 * back without using a keyboard shortcut.
 *
 * Active contour drives which sidebar variant renders. Phase 4 wires
 * the "Files" contour to swap in <FileExplorerPanel /> in place of the
 * boards list. Contour and collapse state persist to localStorage.
 *
 * Selecting the Files contour also navigates to `/files`; selecting
 * Operational navigates back to `/boards`. This keeps the URL the
 * authoritative source so deep links work.
 */
import { useLocation, useNavigate } from '@tanstack/react-router';
import { ChevronsRight } from 'lucide-react';
import type { ReactNode } from 'react';
import * as React from 'react';

import { ArchitectSidebar } from '../architect/architect-sidebar';
import { ConstitutionSidebar } from '../constitution/constitution-sidebar';
import { DepartmentsSidebar } from '../departments/departments-sidebar';
import { FilesSidebar } from '../files/files-sidebar';
import { UniversitySidebar } from '../university/university-sidebar';
import { ActivityBar, type ActivityContour } from './activity-bar';
import { Sidebar } from './sidebar';

const COLLAPSE_STORAGE_KEY = 'chaos-sidebar-collapsed';
const CONTOUR_STORAGE_KEY = 'chaos-active-contour';

function readCollapsed(): boolean {
  try {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(COLLAPSE_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeCollapsed(value: boolean): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(COLLAPSE_STORAGE_KEY, String(value));
  } catch {
    /* ignore */
  }
}

function readContour(): ActivityContour {
  try {
    if (typeof localStorage === 'undefined') return 'operational';
    const raw = localStorage.getItem(CONTOUR_STORAGE_KEY);
    if (
      raw === 'operational' ||
      raw === 'files' ||
      raw === 'architect' ||
      raw === 'departments' ||
      raw === 'learning' ||
      raw === 'knowledge' ||
      raw === 'strategy' ||
      raw === 'constitution'
    ) {
      return raw;
    }
    return 'operational';
  } catch {
    return 'operational';
  }
}

function writeContour(value: ActivityContour): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(CONTOUR_STORAGE_KEY, value);
  } catch {
    /* ignore */
  }
}

export function Shell({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = React.useState<boolean>(() => readCollapsed());
  const [contour, setContourState] = React.useState<ActivityContour>(() => readContour());
  const navigate = useNavigate();
  const location = useLocation();

  // Mirror URL → contour so deep links land on the correct sidebar variant.
  // Triggered on every pathname change.
  React.useEffect(() => {
    const path = location.pathname;
    let derived: ActivityContour | null = null;
    if (path.startsWith('/architect')) derived = 'architect';
    else if (path.startsWith('/departments')) derived = 'departments';
    else if (path.startsWith('/files')) derived = 'files';
    else if (path.startsWith('/university')) derived = 'learning';
    else if (
      path.startsWith('/constitution') ||
      path.startsWith('/mission') ||
      path.startsWith('/principles')
    )
      derived = 'constitution';
    else if (path.startsWith('/strategy') || path.startsWith('/goals')) derived = 'strategy';
    else if (path.startsWith('/board') || path.startsWith('/boards') || path.startsWith('/project'))
      derived = 'operational';
    if (derived && derived !== contour) {
      setContourState(derived);
      writeContour(derived);
    }
  }, [location.pathname, contour]);

  const toggle = React.useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      writeCollapsed(next);
      return next;
    });
  }, []);

  const setContour = React.useCallback(
    (c: ActivityContour) => {
      setContourState(c);
      writeContour(c);
      // Mirror the contour into the URL so deep links survive a reload.
      if (c === 'files') {
        void navigate({ to: '/files' });
      } else if (c === 'operational') {
        void navigate({ to: '/boards' });
      } else if (c === 'architect') {
        void navigate({ to: '/architect' });
      } else if (c === 'departments') {
        void navigate({ to: '/departments' });
      } else if (c === 'learning') {
        void navigate({ to: '/university' });
      } else if (c === 'constitution') {
        void navigate({ to: '/constitution' });
      } else if (c === 'strategy') {
        void navigate({ to: '/strategy' });
      }
    },
    [navigate],
  );

  return (
    <div className="relative flex h-screen w-full overflow-hidden bg-background">
      <ActivityBar active={contour} onSelect={setContour} />

      {contour === 'files' ? (
        <FilesSidebar collapsed={collapsed} onToggle={toggle} />
      ) : contour === 'architect' ? (
        <ArchitectSidebar collapsed={collapsed} onToggle={toggle} />
      ) : contour === 'departments' ? (
        <DepartmentsSidebar collapsed={collapsed} onToggle={toggle} />
      ) : contour === 'learning' ? (
        <UniversitySidebar collapsed={collapsed} onToggle={toggle} />
      ) : contour === 'constitution' || contour === 'strategy' ? (
        <ConstitutionSidebar collapsed={collapsed} onToggle={toggle} />
      ) : (
        <Sidebar collapsed={collapsed} onToggle={toggle} />
      )}

      {/* Floating expand handle shown while the rail is hidden */}
      {collapsed && (
        <button
          type="button"
          onClick={toggle}
          aria-label="Open sidebar"
          className="absolute left-14 top-3 z-20 flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
        >
          <ChevronsRight className="h-4 w-4" aria-hidden />
        </button>
      )}

      <main className="flex-1 overflow-y-auto">{children}</main>
    </div>
  );
}
